// ============================================================================
// PERMISSION ENGINE — Pure TypeScript, Zero React Dependencies
// ============================================================================
// Builds O(1) lookup Maps from UserPermissionContext at login time.
// Fully unit-testable with plain describe/it — no React imports needed.
// Can be replaced with CASL.js later without touching UI layer.

import type {
  UserPermissionContext,
  AccessibleModule,
  AccessibleMenu,
  AccessibleUI,
  AccessibleElement,
  ElementMasking,
} from '../api/types/auth.types';

// ============================================================================
// Types
// ============================================================================

export interface ElementPermission {
  isVisible: boolean;
  isEnabled: boolean;
  allowedActions: Set<string>;
  masking: ElementMasking | null;
}

export interface PermissionEngine {
  // Module-level (presence = access; modules have NO allowedActions in tenant-hub)
  hasModuleAccess(moduleCode: string): boolean;
  getModule(moduleCode: string): AccessibleModule | null;

  // Menu-level (menus HAVE allowedActions)
  hasMenuAccess(menuCode: string): boolean;
  getMenuActions(menuCode: string): Set<string>;

  // UI-level (UIs HAVE allowedActions)
  hasUIAccess(uiCode: string): boolean;
  getUIActions(uiCode: string): Set<string>;

  // Element-level
  getElementState(elementCode: string): ElementPermission | null;
  isElementVisible(elementCode: string): boolean;
  isElementEnabled(elementCode: string): boolean;

  // Generic action check (searches menu -> UI -> element levels)
  hasAction(resourceCode: string, action: string): boolean;

  // Raw context access
  getContext(): UserPermissionContext | null;
}

// ============================================================================
// Navigation Types
// ============================================================================

export interface NavigationItem {
  id: string;
  code: string;
  label: string;
  icon: string | null;
  path: string | null;
  type: string;
  displayOrder: number;
  children: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  code: string;
  label: string;
  icon: string | null;
  displayOrder: number;
  items: NavigationItem[];
}

export interface ModuleConfig {
  moduleCode: string;
  moduleName: string;
  icon: string | null;
  menus: Record<string, {
    menuCode: string;
    menuName: string;
    menuItemId: string;
    navigationPath: string | null;
    primaryScreenCode: string | null;
  }>;
}

// ============================================================================
// Factory Function
// ============================================================================

export function createPermissionEngine(context: UserPermissionContext | null): PermissionEngine {
  const moduleMap = new Map<string, AccessibleModule>();
  const menuMap = new Map<string, { actions: Set<string>; menu: AccessibleMenu }>();
  const uiMap = new Map<string, { actions: Set<string>; ui: AccessibleUI }>();
  const elementMap = new Map<string, ElementPermission>();

  if (context) {
    for (const mod of context.modules) {
      moduleMap.set(mod.moduleCode, mod);

      const flatMenus = flattenMenuHierarchy(mod.menus);
      for (const menu of flatMenus) {
        menuMap.set(menu.menuCode, {
          actions: new Set(menu.allowedActions),
          menu,
        });

        if (menu.uIs) {
          for (const ui of menu.uIs) {
            uiMap.set(ui.uiCode, {
              actions: new Set(ui.allowedActions),
              ui,
            });

            for (const el of flattenElements(ui.elements)) {
              elementMap.set(el.elementCode, {
                isVisible: el.isVisible,
                isEnabled: el.isEnabled,
                allowedActions: new Set(el.allowedActions),
                masking: el.masking?.isMasked ? el.masking : null,
              });
            }
          }
        }
      }
    }
  }

  return {
    hasModuleAccess: (code) => moduleMap.has(code),
    getModule: (code) => moduleMap.get(code) ?? null,

    hasMenuAccess: (code) => menuMap.has(code),
    getMenuActions: (code) => menuMap.get(code)?.actions ?? new Set(),

    hasUIAccess: (code) => uiMap.has(code),
    getUIActions: (code) => uiMap.get(code)?.actions ?? new Set(),

    getElementState: (code) => elementMap.get(code) ?? null,
    isElementVisible: (code) => elementMap.get(code)?.isVisible ?? true,
    isElementEnabled: (code) => elementMap.get(code)?.isEnabled ?? true,

    hasAction: (code, action) => {
      // Check menu -> UI -> element levels (modules have no actions)
      return (
        menuMap.get(code)?.actions.has(action) ??
        uiMap.get(code)?.actions.has(action) ??
        elementMap.get(code)?.allowedActions.has(action) ??
        false
      );
    },

    getContext: () => context,
  };
}

// ============================================================================
// Navigation Builder (Pure)
// ============================================================================

export function buildNavigationFromPermissions(
  context: UserPermissionContext
): NavigationSection[] {
  return context.modules
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((mod) => ({
      id: mod.id,
      code: mod.moduleCode,
      label: mod.moduleName,
      icon: mod.icon,
      displayOrder: mod.displayOrder,
      items: buildMenuItems(mod.menus),
    }));
}

function buildMenuItems(menus: AccessibleMenu[]): NavigationItem[] {
  return menus
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((menu) => ({
      id: menu.id,
      code: menu.menuCode,
      label: menu.menuName,
      icon: menu.icon,
      path: menu.navigationPath ?? menu.primaryScreen?.uiPath ?? null,
      type: menu.menuType,
      displayOrder: menu.displayOrder,
      children: buildMenuItems(menu.children),
    }));
}

export function buildModulesConfigFromPermissions(
  context: UserPermissionContext
): Record<string, ModuleConfig> {
  const config: Record<string, ModuleConfig> = {};

  for (const mod of context.modules) {
    const menus: ModuleConfig['menus'] = {};
    const flatMenus = flattenMenuHierarchy(mod.menus);

    for (const menu of flatMenus) {
      menus[menu.menuCode] = {
        menuCode: menu.menuCode,
        menuName: menu.menuName,
        menuItemId: menu.id,
        navigationPath: menu.navigationPath ?? menu.primaryScreen?.uiPath ?? null,
        primaryScreenCode: menu.primaryScreen?.uiCode ?? null,
      };
    }

    config[mod.moduleCode] = {
      moduleCode: mod.moduleCode,
      moduleName: mod.moduleName,
      icon: mod.icon,
      menus,
    };
  }

  return config;
}

// ============================================================================
// Helpers (Pure)
// ============================================================================

function flattenMenuHierarchy(menus: AccessibleMenu[]): AccessibleMenu[] {
  const result: AccessibleMenu[] = [];
  for (const menu of menus) {
    result.push(menu);
    if (menu.children.length > 0) {
      result.push(...flattenMenuHierarchy(menu.children));
    }
  }
  return result;
}

function flattenElements(elements: AccessibleElement[]): AccessibleElement[] {
  // Elements can have parent-child relationships via parentElementId,
  // but the flat array from tenant-hub already contains all elements.
  // No recursive flattening needed — just return as-is.
  return elements;
}
